<template>
  <div>
    <!--------------------------------------
    | read only
    ---------------------------------------->
    <template v-if="(readOnly || isDatasetReadOnly) && approvedDataType">
      <VisibleText>
        {{ TYPE_TO_READABLE[approvedDataType] || SCALE_TYPE_TO_READABLE[approvedDataType] }}
      </VisibleText>
    </template>

    <!--------------------------------------
    | editing
    ---------------------------------------->
    <template v-if="!readOnly && !isDatasetReadOnly">
      <VisibleText class="dataset-table__type-noresponses" v-if="noResponses">
        No responses. Setting data type unavailable.
      </VisibleText>
      <div
        :class="[
          'dataset-table__type-select select-wrapper',
          { 'dataset-table__type-select--withicon': withicon },
        ]"
      >
        <label
          class="sr-only"
          :for="`edit-data-type-${clientQuestion._id.$oid}`"
          :data-parent="$options.name"
        >
          {{ translate('select data type', $options.name) }}
        </label>
        <select
          :class="['select', { 'dataset-table__type-unknown': unknown }]"
          :id="`edit-data-type-${clientQuestion._id.$oid}`"
          :disabled="noResponses"
          tabindex="-1"
          v-model="$data.$_approvedDataType"
          @change="onChange"
          style="width: 100%"
        >
          <option disabled value="" :data-parent="$options.name">
            {{ translate('select type', $options.name) }}
          </option>
          <option value="none" :data-parent="$options.name">
            {{ translate('none', $options.name) }}
          </option>
          <optgroup label="Data Types">
            <option
              v-for="type in Object.keys(TYPE_TO_READABLE)"
              :key="type"
              :value="type"
              :data-parent="$options.name"
            >
              {{ translate(TYPE_TO_READABLE[type], $options.name) }}
            </option>
          </optgroup>
          <optgroup label="Scale Types">
            <option
              v-for="scale in Object.keys(SCALE_TYPE_TO_READABLE)"
              :key="scale"
              :value="scale"
              :data-parent="$options.name"
            >
              {{ translate(SCALE_TYPE_TO_READABLE[scale]) }}
            </option>
          </optgroup>
        </select>
        <SvgIconMeaningful
          class="dataset-table__type-icon"
          icon="question"
          title="unknown data type"
          v-if="unknown"
        />
        <SvgIconMeaningful
          class="dataset-table__type-icon"
          icon="warning"
          title="no responses"
          v-if="noResponses"
        />
      </div>
    </template>
    <div
      v-if="
        approvedDataType && approvedDataType == 'OPEN_ENDED' && activeSection == 'DatasetCleaner'
      "
    >
      <TextAIMiniGuide
        @closingGuide="showTextAIInfo = false"
        :showInfo="showTextAIInfo"
        :avoidLeftOffset="true"
      >
      </TextAIMiniGuide>
      <div v-if="askUserToConfirmText">
        <div
          style="display: flex; flex-direction: column"
          class="message__block message__block--warning"
        >
          <div style="display: flex; flex-direction: row">
            <div
              class="project-analysis-item__header-wrapper-btn"
              style="display: flex; font-weight: 200"
            >
              <VisibleText
                >Text questions need to be formatted
                <SvgIconDecorative icon="error" />
              </VisibleText>
              <button
                class="btn-secondary"
                style="height: 1rem; padding-top: 0.3rem"
                @click="confirmThisText"
              >
                <VisibleText style="color: var(--base-colour); text-decoration: underline"
                  >Click here to confirm this is a text question</VisibleText
                >
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <div v-if="tempConfirmingText">
          <div
            style="display: flex; flex-direction: column"
            class="message__block message__block--warning"
          >
            <div style="display: flex; flex-direction: row">
              <div
                class="project-analysis-item__header-wrapper-btn"
                style="display: flex; font-weight: 200"
              >
                <VisibleText
                  >Text is being generated. Please wait.
                  <SvgIconDecorative icon="error" />
                </VisibleText>
                <span class="also-found__loading-spinner spinner"></span>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <div v-if="clientQuestion.is_adding_all_responses == true">
            <div
              style="display: flex; flex-direction: column"
              class="message__block message__block--warning"
            >
              <div style="display: flex; flex-direction: row">
                <div
                  class="project-analysis-item__header-wrapper-btn"
                  style="display: flex; font-weight: 200"
                >
                  <VisibleText
                    >Text is still be generated. Please refresh the page until this is complete.
                  </VisibleText>
                  <SvgIconDecorative icon="error" />
                  <span
                    style="height: 1rem; width: 2rem; margin-top: 1rem"
                    class="also-found__loading-spinner spinner"
                  ></span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="clientQuestion.is_adding_all_responses == false">
            <div
              style="display: flex; flex-direction: column"
              class="message__block message__block--information"
            >
              <div style="display: flex; flex-direction: row">
                <div
                  class="project-analysis-item__header-wrapper-btn"
                  style="display: flex; font-weight: 200"
                >
                  <SvgIconDecorative icon="success" style="margin: auto" />
                  <VisibleText style="margin: auto">Basic text analysis enabled </VisibleText>
                  <div v-if="textAIStatus == 'not started'">
                    <div v-if="isLoading">
                      <span class="also-found__loading-spinner spinner"></span>
                    </div>
                    <button
                      class="btn-secondary"
                      style=""
                      @click="preprocessThisQuestion"
                      v-if="!isDatasetReadOnly && !isLoading"
                    >
                      <SvgIconDecorative icon="clearCircle" style="margin: auto" />
                      <VisibleText style="color: var(--base-colour); text-decoration: underline"
                        >Click here to enable AI text analysis</VisibleText
                      >
                    </button>
                  </div>
                  <div style="margin: auto" v-if="textAIStatus == 'complete'">
                    <SvgIconDecorative icon="success" />
                    <VisibleText>AI text analysis enabled</VisibleText>
                  </div>
                  <div style="margin-left: 1rem" v-if="textAIStatus == 'processing'">
                    <SvgIconDecorative icon="clearCircle" style="margin: auto" />
                    <VisibleText style="margin: auto">Processing AI text analysis...</VisibleText>
                  </div>
                  <div v-if="textAIStatus == 'error'">
                    <button
                      class="btn-secondary"
                      style=""
                      @click="preprocessThisQuestion"
                      v-if="!isDatasetReadOnly"
                    >
                      <SvgIconDecorative icon="clearCircle" style="margin: auto" />
                      <VisibleText style="color: var(--base-colour); text-decoration: underline"
                        >An error occured. Try again in a few minutes.</VisibleText
                      >
                    </button>
                  </div>
                  <button
                    style="
                      background-color: transparent;
                      font-family: Core-Sans;
                      border-color: transparent;
                      color: var(--primary);
                      margin-bottom: 0.1rem;
                      margin-left: 0.5rem;
                    "
                    @click="showTextAIInfo = !showTextAIInfo"
                  >
                    <strong>Learn more</strong>
                  </button>

                  <div
                    v-if="clientQuestion.added_count && clientQuestion.added_all_responses == false"
                    style="padding-left: 0.5rem"
                  >
                    <VisibleText
                      >Your text is too large. Our current version is optimized for 5,000,000
                      characters. Only &nbsp;</VisibleText
                    >
                    <span>{{ clientQuestion.added_count }}</span>
                    <VisibleText>&nbsp;responses were generated.</VisibleText>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Components
import SvgIconMeaningful from '@/components/UI/Svg/SvgIconMeaningful.vue';
import SvgIconDecorative from '@/components/UI/Svg/SvgIconDecorative.vue';
import TextAIMiniGuide from '@/components/UserGuide/TextAIMiniGuide.vue';

// Mixins
import DatasetDetailsMixin from '@/utils/mixins/datasetDetailsMixin.js';
import DataTypeMixin from '@/components/Dataset/Mixins/datatypeMixin.js';
import UserMixin from '@/utils/mixins/userMixin.js';

export default {
  name: 'DataType',
  mixins: [DatasetDetailsMixin, DataTypeMixin, UserMixin],
  components: {
    SvgIconMeaningful,
    SvgIconDecorative,
    TextAIMiniGuide,
  },
  props: {
    activeSection: {
      type: String,
      default: () => '',
    },
    clientQuestion: {
      default: () => {},
      type: Object,
      required: true,
    },
    suggestedType: {
      defauly: null,
      type: String,
      default: () => '',
    },
    readOnly: {
      default: false,
      type: Boolean,
    },
    isDatasetReadOnly: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      $_approvedDataType: '', // have a local copy of the approved data type
      approvedDataType: '',
      unknown: false,
      askUserToConfirmText: false,
      tempConfirmingText: false,
      local_suggested_data_type: '',
      // check text AI status
      counter: 0,
      timer: null,
      showTextAIInfo: false,
      isLoading: false,
    };
  },
  created() {
    if (this.clientQuestion.approved_data_type) {
      this.setLocalApprovedDataType(this.clientQuestion.approved_data_type);

      if (
        this.clientQuestion.approved_data_type == 'OPEN_ENDED' &&
        !Object.keys(this.clientQuestion).includes('added_count')
      ) {
        this.askUserToConfirmText = true;
      }
    }
  },
  computed: {
    clientQuestionId() {
      return this.clientQuestion._id.$oid;
    },
    noResponses() {
      if (
        this.clientQuestion.approved_data_type !== 'OPEN_ENDED' &&
        this.clientQuestion.unique_values.length === 0
      ) {
        return true;
      }
      return false;
    },
    withicon() {
      return this.noResponses || this.unknown;
    },
    textAIStatus() {
      if (
        this.clientQuestion.text_preprocessing &&
        this.clientQuestion.text_preprocessing.complete
      ) {
        return 'complete';
      }
      if (
        this.clientQuestion.text_preprocessing &&
        this.clientQuestion.text_preprocessing.processing
      ) {
        return 'processing';
      }
      if (this.clientQuestion.text_preprocessing && this.clientQuestion.text_preprocessing.error) {
        return 'error';
      }
      return 'not started';
    },
  },
  methods: {
    async confirmThisText() {
      this.askUserToConfirmText = false;
      this.tempConfirmingText = true;
      await this.$services.DATASETS_SERVICE.updateClientQuestion(this.clientQuestion._id.$oid, {
        approved_data_type: 'OPEN_ENDED',
        local_suggested_data_type: this.clientQuestion.suggested_data_type,
        scale_range: null,
        unique_values: [],
      });
      this.tempConfirmingText = false;
      this.updateClientQuestions(this.dataset._id.$oid);
    },
    preprocessThisQuestion() {
      this.isLoading = true;
      this.preprocessThisTextMixin(this.clientQuestion['_id']['$oid']); // Found in datasetDetailsMixin
    },
    onChange() {
      this.saveDataType(this.$data.$_approvedDataType, true);
      this.approvedDataType = this.$data.$_approvedDataType;
      this.$emit('on-change-data-type', this.$data.$_approvedDataType);
    },
    setLocalApprovedDataType(dataType) {
      this.$data.$_approvedDataType = dataType && dataType.length > 0 ? dataType : '';
      this.approvedDataType = dataType;
    },
    async saveDataType(dataType, updateStore = false) {
      if (
        !this.readOnly &&
        this.permissions.includes('update-dataset') &&
        !['CAT_OR_SCALE'].includes(dataType)
      ) {
        await this.$_dataTypeMixin_saveDataType(this.clientQuestion, dataType, updateStore);
        this.$emit('approved-data-type');
        this.unknown = false;
        if (dataType == 'DATE') {
          this.$emit('autoSelectDate');
        }
      }
    },
    // for text AI status checking
    incrementCounter() {
      this.counter++;
    },
    startTimer() {
      this.isLoading = false;
      this.timer = setTimeout(
        function repeat() {
          this.checkAIProcessingMixin(this.clientQuestion._id.$oid);
          this.incrementCounter();
          this.timer = setTimeout(repeat.bind(this), 2000); // Adjust interval as needed
        }.bind(this),
        1000,
      );
    },
    stopTimer() {
      clearTimeout(this.timer);
    },
  },
  beforeUnmount() {
    this.stopTimer();
  },
  watch: {
    clientQuestionId: {
      immediate: false,
      handler: function () {
        this.setLocalApprovedDataType(this.clientQuestion.approved_data_type);
      },
    },
    textAIStatus: {
      immediate: true,
      handler: function (status) {
        if (status && (status == 'complete' || status == 'error')) {
          this.stopTimer();
        }
        if (status && status == 'processing') {
          this.startTimer();
        }
      },
    },
    suggestedType: {
      immediate: true,
      handler: function (suggestedType) {
        if (suggestedType && suggestedType === 'CAT_OR_SCALE') {
          this.local_suggested_data_type = suggestedType;
          this.unknown = true;
        } else if (suggestedType) {
          this.local_suggested_data_type = suggestedType;
          this.setLocalApprovedDataType(suggestedType);
          this.saveDataType(suggestedType); // auto save suggested type
        }
      },
    },
  },
};
</script>
